import React, { useState, useEffect } from 'react';
import {
  BarChart3, Building2, Users, CheckCircle, Trash2, Play, Pause, Zap,
  UserPlus, X, MessageSquare, Bug, Lightbulb, Activity,
} from 'lucide-react';
import {
  fetchMetrics, fetchTenants, fetchAdmins, fetchAIEvents,
  fetchFeedback, updateFeedbackStatus, fetchCohorts,
  inviteAdmin, removeAdmin,
  updateTenantStatus, deleteTenant,
  PlatformMetrics, TenantSummary, PlatformAdmin, AIEvent, FeedbackItem,
  TenantFunnel, CohortRow,
} from '../services/api';
import { TenantDetailPage } from './TenantDetailPage';

type Tab = 'dashboard' | 'tenants' | 'users' | 'feedback';

export const DashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [aiEvents, setAiEvents] = useState<AIEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Get current user email from JWT
  const currentUserEmail = (() => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.email || '';
    } catch { return ''; }
  })();

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [m, t, a, ai] = await Promise.all([
        fetchMetrics(),
        fetchTenants(),
        fetchAdmins(),
        fetchAIEvents(20).catch(() => []),
      ]);
      setMetrics(m);
      setTenants(t);
      setAdmins(a);
      setAiEvents(ai);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleStatusChange = async (tenantId: string, newStatus: string) => {
    try {
      await updateTenantStatus(tenantId, newStatus);
      await loadData();
    } catch {
      setError('Failed to update tenant status');
    }
  };

  const handleDelete = async (tenantId: string, tenantName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${tenantName}"? This will soft-delete the tenant.`)) {
      return;
    }
    try {
      await deleteTenant(tenantId);
      await loadData();
    } catch {
      setError('Failed to delete tenant');
    }
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={18} /> },
    { id: 'tenants', label: 'Tenants', icon: <Building2 size={18} /> },
    { id: 'users', label: 'Admins', icon: <Users size={18} /> },
    { id: 'feedback', label: 'Feedback', icon: <MessageSquare size={18} /> },
  ];

  if (selectedTenantId) {
    return (
      <TenantDetailPage
        tenantId={selectedTenantId}
        onBack={() => setSelectedTenantId(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
      {/* Tab bar */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-1 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        </div>
      )}

      {aiEvents.length > 0 && activeTab === 'dashboard' && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <AIEventsAlert events={aiEvents} />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'dashboard' && metrics && (
          <>
            <MetricsView metrics={metrics} />
            <CohortView />
          </>
        )}
        {activeTab === 'tenants' && (
          <TenantsView
            tenants={tenants}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            onSelect={(id) => setSelectedTenantId(id)}
            onRefresh={async (includeDeleted) => {
              const t = await fetchTenants(includeDeleted);
              setTenants(t);
            }}
          />
        )}
        {activeTab === 'users' && (
          <AdminsView
            admins={admins}
            currentUserEmail={currentUserEmail}
            onInvite={async (email) => {
              try {
                await inviteAdmin(email);
                const a = await fetchAdmins();
                setAdmins(a);
              } catch (err: any) {
                setError(err.response?.data?.detail || 'Failed to invite admin');
              }
            }}
            onRemove={async (email) => {
              try {
                await removeAdmin(email);
                const a = await fetchAdmins();
                setAdmins(a);
              } catch (err: any) {
                setError(err.response?.data?.detail || 'Failed to remove admin');
              }
            }}
          />
        )}
        {activeTab === 'feedback' && <FeedbackView />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dashboard tab — metric cards
// ---------------------------------------------------------------------------

const MetricsView: React.FC<{ metrics: PlatformMetrics }> = ({ metrics }) => {
  const wauT = metrics.wauTenants ?? 0;
  const mauT = metrics.mauTenants ?? 0;
  const wauU = metrics.wauUsers ?? 0;
  const wauPct = metrics.totalTenants > 0 ? Math.round((wauT / metrics.totalTenants) * 100) : 0;

  const cards = [
    { label: 'Total Tenants', value: metrics.totalTenants, icon: <Building2 size={24} />, color: 'bg-blue-50 text-blue-600' },
    { label: 'Active', value: metrics.activeTenants, icon: <CheckCircle size={24} />, color: 'bg-green-50 text-green-600' },
    {
      label: 'Active 7d',
      value: `${wauT} / ${metrics.totalTenants}`,
      sub: `${wauPct}% • ${mauT} 30d • ${wauU} users 7d`,
      icon: <Activity size={24} />,
      color: 'bg-amber-50 text-amber-600',
    },
    { label: 'Total Users', value: metrics.totalPlatformUsers, icon: <Users size={24} />, color: 'bg-purple-50 text-purple-600' },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card) => (
        <div key={card.label} className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-500">{card.label}</span>
            <div className={`p-2 rounded-lg ${card.color}`}>{card.icon}</div>
          </div>
          <div className="text-3xl font-bold text-gray-900">{card.value}</div>
          {('sub' in card) && card.sub && (
            <div className="text-xs text-gray-500 mt-1">{card.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cohort retention table
// ---------------------------------------------------------------------------

const CohortView: React.FC = () => {
  const [rows, setRows] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCohorts(8)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (rows.length === 0) return null;

  const maxWeek = Math.max(0, ...rows.map((r) => r.weeks.length - 1));
  const heatColor = (active: number, size: number) => {
    if (size === 0) return 'bg-gray-50 text-gray-300';
    const pct = active / size;
    if (pct >= 0.75) return 'bg-green-500 text-white';
    if (pct >= 0.5) return 'bg-green-300 text-gray-900';
    if (pct >= 0.25) return 'bg-yellow-200 text-gray-900';
    if (pct > 0) return 'bg-orange-200 text-gray-900';
    return 'bg-gray-100 text-gray-500';
  };

  return (
    <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Cohort retention (active tenants by signup week)</h3>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead>
            <tr>
              <th className="text-left px-2 py-1 text-gray-500 font-medium">Cohort</th>
              <th className="text-right px-2 py-1 text-gray-500 font-medium">Size</th>
              {Array.from({ length: maxWeek + 1 }).map((_, i) => (
                <th key={i} className="text-center px-2 py-1 text-gray-500 font-medium">W{i}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = new Date(r.weekStart);
              const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              return (
                <tr key={r.weekStart}>
                  <td className="px-2 py-1 text-gray-700">{label}</td>
                  <td className="px-2 py-1 text-right text-gray-500">{r.size}</td>
                  {Array.from({ length: maxWeek + 1 }).map((_, i) => {
                    const w = r.weeks.find((x) => x.week === i);
                    if (!w) return <td key={i} className="px-2 py-1" />;
                    return (
                      <td key={i} className="px-1 py-1">
                        <div className={`w-10 h-7 rounded flex items-center justify-center font-medium ${heatColor(w.active, r.size)}`}>
                          {w.active}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Funnel strip
// ---------------------------------------------------------------------------

const FUNNEL_STEPS: { key: keyof TenantFunnel; label: string; short: string; desc: string }[] = [
  {
    key: 'signedUp',
    label: 'Signed up',
    short: 'S',
    desc: 'Tenant account created (always true for any tenant in this list).',
  },
  {
    key: 'importCompleted',
    label: 'Imported employees',
    short: 'I',
    desc: 'Tenant has imported at least one employee — via CSV upload, HR sync, or manual creation. Proves they got past the empty-state.',
  },
  {
    key: 'domainSharing',
    label: 'Domain sharing enabled',
    short: 'D',
    desc: 'Tenant has switched access mode to "domain" and added at least one allowed domain — anyone from that domain can sign in. Strong signal of intent to roll out beyond a single user.',
  },
  {
    key: 'connectionAdded',
    label: 'Connected an HR system',
    short: 'C',
    desc: 'Tenant has added at least one HR connection (BambooHR / BreatheHR / etc.). Indicates intent to sync rather than one-off CSV use.',
  },
  {
    key: 'twoUsers',
    label: '2+ users',
    short: 'U',
    desc: 'At least 2 users have access to this tenant. Single-user tenants rarely retain — getting a second user is a leading retention indicator.',
  },
  {
    key: 'syncCompleted',
    label: 'HR sync completed',
    short: 'Y',
    desc: 'A real HR sync has finished successfully (not just a connection added). Proves the integration actually works for them — the deepest activation step.',
  },
];

export const FunnelStrip: React.FC<{ funnel?: TenantFunnel; size?: 'sm' | 'md' }> = ({ funnel, size = 'sm' }) => {
  if (!funnel) return <span className="text-xs text-gray-400">-</span>;
  const dotSize = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-7 h-7 text-xs';
  return (
    <div className="flex items-center gap-1">
      {FUNNEL_STEPS.map((s) => {
        const done = funnel[s.key];
        return (
          <div key={s.key} className="relative group">
            <div
              className={`${dotSize} flex items-center justify-center rounded-full font-bold cursor-help ${
                done ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-400'
              }`}
            >
              {s.short}
            </div>
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 hidden group-hover:block w-64 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
              <div className="font-semibold mb-1">
                {s.label} — {done ? '✅ done' : '◯ not yet'}
              </div>
              <div className="text-gray-300 leading-snug">{s.desc}</div>
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tenants tab
// ---------------------------------------------------------------------------

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-amber-100 text-amber-800',
    deleted: 'bg-red-100 text-red-800',
    trial: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

type SortField = 'name' | 'status' | 'employeeCount' | 'userCount' | 'createdAt' | 'lastAdminLoginAt' | 'lastUserLoginAt' | 'activeUsers7d' | 'activeUsers30d' | 'lastActionAt';
type SortDir = 'asc' | 'desc';

const TenantsView: React.FC<{
  tenants: TenantSummary[];
  onStatusChange: (id: string, status: string) => void;
  onDelete: (id: string, name: string) => void;
  onSelect: (id: string) => void;
  onRefresh: (includeDeleted: boolean) => void;
}> = ({ tenants, onStatusChange, onDelete, onSelect, onRefresh }) => {
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [search, setSearch] = useState('');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'asc' : 'desc');
    }
  };

  const SortHeader: React.FC<{ field: SortField; label: string; align?: string }> = ({ field, label, align }) => (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}
    </th>
  );

  const filtered = tenants
    .filter((t) => {
      if (statusFilter && t.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!t.name.toLowerCase().includes(s) && !t.slug.toLowerCase().includes(s) && !(t.ownerEmail || '').toLowerCase().includes(s)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'status') cmp = a.status.localeCompare(b.status);
      else if (sortField === 'employeeCount') cmp = a.employeeCount - b.employeeCount;
      else if (sortField === 'userCount') cmp = a.userCount - b.userCount;
      else if (sortField === 'createdAt') cmp = (a.createdAt || '').localeCompare(b.createdAt || '');
      else if (sortField === 'lastAdminLoginAt') cmp = (a.lastAdminLoginAt || '').localeCompare(b.lastAdminLoginAt || '');
      else if (sortField === 'lastUserLoginAt') cmp = (a.lastUserLoginAt || '').localeCompare(b.lastUserLoginAt || '');
      else if (sortField === 'activeUsers7d') cmp = (a.activeUsers7d ?? 0) - (b.activeUsers7d ?? 0);
      else if (sortField === 'activeUsers30d') cmp = (a.activeUsers30d ?? 0) - (b.activeUsers30d ?? 0);
      else if (sortField === 'lastActionAt') cmp = (a.lastActionAt || '').localeCompare(b.lastActionAt || '');
      return sortDir === 'asc' ? cmp : -cmp;
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, slug, or owner..."
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending_claim">Pending claim</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => {
              setShowDeleted(e.target.checked);
              onRefresh(e.target.checked);
            }}
            className="w-4 h-4 rounded border-gray-300 text-indigo-600"
          />
          Show deleted
        </label>
        <span className="text-sm text-gray-500">{filtered.length} tenant{filtered.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader field="name" label="Name" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                <SortHeader field="status" label="Status" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                <SortHeader field="userCount" label="Users" align="right" />
                <SortHeader field="employeeCount" label="Employees" align="right" />
                <SortHeader field="activeUsers7d" label="Active 7d" align="right" />
                <SortHeader field="activeUsers30d" label="Active 30d" align="right" />
                <SortHeader field="lastActionAt" label="Last action" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funnel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Terms</th>
                <SortHeader field="createdAt" label="Created" />
                <SortHeader field="lastAdminLoginAt" label="Last admin login" />
                <SortHeader field="lastUserLoginAt" label="Last user login" />
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={17} className="px-4 py-8 text-center text-gray-500">No tenants found</td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">
                      <button
                        onClick={() => onSelect(t.id)}
                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        {t.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{t.slug}</td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{t.plan}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{t.ownerEmail || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-right">{t.userCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-right">{t.employeeCount}</td>
                    <td className={`px-4 py-3 text-sm text-right ${(t.activeUsers7d ?? 0) > 0 ? 'text-green-700 font-medium' : 'text-gray-400'}`}>{t.activeUsers7d ?? 0}</td>
                    <td className={`px-4 py-3 text-sm text-right ${(t.activeUsers30d ?? 0) > 0 ? 'text-gray-700' : 'text-gray-400'}`}>{t.activeUsers30d ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-500" title={t.lastActionAt || ''}>
                      {t.lastActionAt ? new Date(t.lastActionAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3"><FunnelStrip funnel={t.funnel} /></td>
                    <td className="px-4 py-3 text-sm">
                      {t.accessMode === 'domain' && (t.allowedDomains?.length ?? 0) > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
                          title={`Domain access: ${t.allowedDomains?.join(', ')}`}
                        >
                          🌐 {t.allowedDomains?.length} {t.allowedDomains?.length === 1 ? 'domain' : 'domains'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">invite-only</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {t.termsAcceptedAt ? (
                        <span className="inline-flex items-center gap-1 text-green-700" title={`v${t.termsVersion} on ${new Date(t.termsAcceptedAt).toLocaleDateString()}`}>
                          <CheckCircle size={14} /> v{t.termsVersion}
                        </span>
                      ) : (
                        <span className="text-amber-600">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500" title={t.lastAdminLoginAt || ''}>
                      {t.lastAdminLoginAt ? new Date(t.lastAdminLoginAt).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500" title={t.lastUserLoginAt || ''}>
                      {t.lastUserLoginAt ? new Date(t.lastUserLoginAt).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {t.status === 'active' ? (
                          <button
                            onClick={() => onStatusChange(t.id, 'suspended')}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition"
                            title="Suspend"
                          >
                            <Pause size={16} />
                          </button>
                        ) : t.status === 'suspended' ? (
                          <button
                            onClick={() => onStatusChange(t.id, 'active')}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition"
                            title="Activate"
                          >
                            <Play size={16} />
                          </button>
                        ) : null}
                        {t.status !== 'deleted' && (
                          <button
                            onClick={() => onDelete(t.id, t.name)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------

const AdminsView: React.FC<{
  admins: PlatformAdmin[];
  currentUserEmail: string;
  onInvite: (email: string) => Promise<void>;
  onRemove: (email: string) => Promise<void>;
}> = ({ admins, currentUserEmail, onInvite, onRemove }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    await onInvite(inviteEmail.trim());
    setInviteEmail('');
    setInviting(false);
  };

  return (
    <div className="space-y-4">
      {/* Invite bar */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            placeholder="Enter email to invite as platform admin..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            onClick={handleInvite}
            disabled={inviting || !inviteEmail.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <UserPlus size={16} />
            {inviting ? 'Inviting...' : 'Invite Admin'}
          </button>
        </div>
      </div>

      {/* Admins table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {admins.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">No platform admins found</td>
                </tr>
              ) : (
                admins.map((a) => {
                  const isCurrentUser = a.email.toLowerCase() === currentUserEmail.toLowerCase();
                  return (
                    <tr key={a.email} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {a.email}
                        {isCurrentUser && (
                          <span className="ml-2 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">you</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{a.name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => onRemove(a.email)}
                          disabled={isCurrentUser}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title={isCurrentUser ? 'Cannot remove yourself' : 'Remove admin'}
                        >
                          <X size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AI Events Alert
// ---------------------------------------------------------------------------

const AIEventsAlert: React.FC<{ events: AIEvent[] }> = ({ events }) => {
  const [expanded, setExpanded] = useState(false);

  // Check if any events are from the last 24 hours
  const recentEvents = events.filter((e) => {
    const eventTime = new Date(e.timestamp).getTime();
    return Date.now() - eventTime < 24 * 60 * 60 * 1000;
  });

  const isRecent = recentEvents.length > 0;

  return (
    <div className={`rounded-lg p-4 ${isRecent ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
      <div className="flex items-start gap-3">
        <Zap size={20} className={isRecent ? 'text-red-600 mt-0.5' : 'text-amber-600 mt-0.5'} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className={`font-semibold text-sm ${isRecent ? 'text-red-800' : 'text-amber-800'}`}>
              {isRecent
                ? `AI API failures detected (${recentEvents.length} in the last 24h)`
                : `${events.length} AI API failure${events.length === 1 ? '' : 's'} logged`
              }
            </h3>
            <button
              onClick={() => setExpanded(!expanded)}
              className={`text-xs font-medium ${isRecent ? 'text-red-600 hover:text-red-800' : 'text-amber-600 hover:text-amber-800'}`}
            >
              {expanded ? 'Hide' : 'Show details'}
            </button>
          </div>
          <p className={`text-sm mt-1 ${isRecent ? 'text-red-700' : 'text-amber-700'}`}>
            {isRecent
              ? 'The Claude AI API is not returning results. Imports will use fallback matching (less accurate). Check your Anthropic API credits.'
              : 'Past AI API failures are logged below. The system fell back to heuristic matching for these operations.'
            }
          </p>
          {expanded && (
            <div className="mt-3 space-y-2">
              {events.map((e) => (
                <div key={e.id} className="bg-white rounded p-3 text-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{e.operation}</span>
                    <span className="text-xs text-gray-500">{new Date(e.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="text-gray-600 text-xs font-mono break-all">{e.error}</div>
                  <div className="flex gap-3 mt-1 text-xs text-gray-400">
                    <span>Model: {e.model}</span>
                    {e.tenantId && <span>Tenant: {e.tenantId}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Feedback tab
// ---------------------------------------------------------------------------

const FeedbackTypeBadge: React.FC<{ type: string }> = ({ type }) => {
  if (type === 'bug_report') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <Bug size={12} /> Bug
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      <Lightbulb size={12} /> Feature
    </span>
  );
};

const FeedbackStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    open: 'bg-yellow-100 text-yellow-800',
    acknowledged: 'bg-blue-100 text-blue-800',
    done: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

const FeedbackView: React.FC = () => {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadFeedback = async (filter?: string) => {
    setLoading(true);
    try {
      const data = await fetchFeedback(filter || undefined);
      setFeedback(data);
    } catch {
      // handled silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFeedback(statusFilter); }, [statusFilter]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateFeedbackStatus(id, newStatus);
      await loadFeedback(statusFilter);
    } catch {
      // handled silently
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Filter:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="done">Done</option>
          <option value="closed">Closed</option>
        </select>
        <span className="text-sm text-gray-500">{feedback.length} item{feedback.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin w-6 h-6 border-4 border-indigo-600 border-t-transparent rounded-full" />
        </div>
      ) : feedback.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
          No feedback submissions yet
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {feedback.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    >
                      <td className="px-4 py-3"><FeedbackTypeBadge type={item.type} /></td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-xs truncate">{item.title}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{item.userName || item.userEmail}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{item.tenantSlug}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{item.page || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={item.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleStatusChange(item.id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="open">Open</option>
                          <option value="acknowledged">Acknowledged</option>
                          <option value="done">Done</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                    </tr>
                    {expandedId === item.id && (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 bg-gray-50">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <FeedbackTypeBadge type={item.type} />
                              <FeedbackStatusBadge status={item.status} />
                            </div>
                            <h4 className="font-medium text-gray-900">{item.title}</h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.description}</p>
                            <div className="flex gap-4 text-xs text-gray-400 pt-2">
                              <span>From: {item.userName} ({item.userEmail})</span>
                              <span>Tenant: {item.tenantSlug}</span>
                              <span>Page: {item.page}</span>
                              <span>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
